import { useState, useCallback, useEffect } from 'react';
import posthog from 'posthog-js';
import supportServices from '../services/supportServices';
// Shared ticket data/actions for the dedicated support page and the support drawer,
// so both stay in sync instead of maintaining two copies of the same API logic.
export default function useSupportTickets({ autoFetch = true } = {}) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetchTickets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await supportServices.getMyTickets();
      const data = response?.data ?? response;
      setTickets(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load tickets. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (autoFetch) fetchTickets();
  }, [autoFetch, fetchTickets]);

  const uploadAttachment = useCallback(async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const uploadRes = await supportServices.uploadAttachment(formData);
    return uploadRes?.data?.id || uploadRes?.id || null;
  }, []);
  const addComment = useCallback(async (ticketId, message) => {
    await supportServices.addComment(ticketId, { message });
    await fetchTickets();
  }, [fetchTickets]);

  const createTicket = useCallback(async ({ title, category, priority, description, files }) => {
    const uploadedIds = await Promise.all(files.map((file) => uploadAttachment(file)));
    const attachmentIds = uploadedIds.filter(Boolean);

    const ticketData = {
      title,
      category,
      priority,
      description,
      attachments: attachmentIds,
      metadata: {
        browser: navigator.userAgent,
        os: navigator.platform,
        session_id: posthog.get_session_id?.() || null,
      },
    };

    await supportServices.createTicket(ticketData);
    await fetchTickets();
  }, [uploadAttachment, fetchTickets]);

  return { tickets, loading, error, fetchTickets, addComment, createTicket };
}
